import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * TagInput - freeform pill/tag input
 *
 * Allows the user to build a list of string tags by typing and confirming with
 * comma, Enter, or Tab. Backspace removes the last tag when the input is empty.
 * Each tag is rendered as a removable pill using the same chip tokens as MultiSelect.
 *
 * Usage:
 * ```tsx
 * <TagInput
 *   tags={domains}
 *   onChange={setDomains}
 *   placeholder="company.com"
 *   id="gcal-internal-domain"
 * />
 * ```
 */
import * as React from 'react';
export function TagInput({ tags, onChange, placeholder, disabled = false, id, name, size, }) {
    const inputId = id || React.useId();
    const [inputValue, setInputValue] = React.useState('');
    const inputRef = React.useRef(null);
    function commit(raw) {
        const value = raw.replace(/,/g, '').trim().toLowerCase();
        if (value && !tags.includes(value)) {
            onChange([...tags, value]);
        }
        setInputValue('');
    }
    function handleChange(e) {
        const val = e.target.value;
        // Commit immediately when the user types a comma
        if (val.endsWith(',')) {
            commit(val);
        }
        else {
            setInputValue(val);
        }
    }
    function handleKeyDown(e) {
        if ((e.key === 'Enter' || e.key === 'Tab') && inputValue.trim()) {
            e.preventDefault();
            commit(inputValue);
        }
        if (e.key === 'Backspace' && inputValue === '' && tags.length > 0) {
            onChange(tags.slice(0, -1));
        }
    }
    function handleBlur() {
        if (inputValue.trim()) {
            commit(inputValue);
        }
    }
    function removeTag(tag) {
        onChange(tags.filter((t) => t !== tag));
        inputRef.current?.focus();
    }
    return (_jsxs("div", { className: [
            'tag-input',
            size === 'comfortable' ? 'tag-input--comfortable' : '',
            disabled ? 'tag-input--disabled' : '',
        ]
            .filter(Boolean)
            .join(' '), onClick: () => inputRef.current?.focus(), children: [tags.map((tag) => (_jsxs("span", { className: "tag-input__chip", children: [_jsx("span", { className: "tag-input__chip-label", children: tag }), !disabled && (_jsx("button", { type: "button", className: "tag-input__chip-remove", onClick: (e) => {
                            e.stopPropagation();
                            removeTag(tag);
                        }, "aria-label": `Remove ${tag}`, children: "\u00D7" }))] }, tag))), _jsx("input", { ref: inputRef, id: inputId, name: name, type: "text", className: "tag-input__input", value: inputValue, placeholder: tags.length === 0 ? placeholder : '', disabled: disabled, autoComplete: "off", onChange: handleChange, onKeyDown: handleKeyDown, onBlur: handleBlur })] }));
}
//# sourceMappingURL=TagInput.js.map